<template>
  <app-page-container :custom="1600" :min-height="0">
    <div class="ui-page">
      <app-filter-toolbar>
        <template #primary><AppInput id="cancellation-search" v-model="query" design="new" type="search" :placeholder="isFinance ? 'Reference or client' : 'Group or client'" aria-label="Search" @input="scheduleSearch" @keydown.enter.native.prevent="search" /></template>
        <AppSelect v-if="canFilterManagers" v-model="manager" design="new" input-id="cancellation-manager" aria-label="Responsible manager" :options="managerOptions" label="full_name" placeholder="Responsible manager" :filterable="false" @search="searchManagers" @open="loadManagers()" @input="search" />
        <AppSelect v-if="isRefunds" v-model="status" design="new" input-id="cancellation-status" aria-label="Refund status" :options="states" :reduce="item => item.code" label="name" placeholder="Refund status" @input="search" />
        <template v-if="hasFilters" #clear><AppClearFiltersButton design="new" icon-only @clear="clear" /></template>
      </app-filter-toolbar>
      <AppErrorState v-if="managerError" :message="managerError" retry-label="Retry managers" @retry="loadManagers" />
      <AppContentSkeleton v-if="loading" label="Loading cancelled groups" :columns="columns" min-width="1200px" />
      <AppErrorState v-else-if="error" :message="error" @retry="load" />
      <template v-else>
        <AppCard v-if="rows.length" design="new" variant="table">
          <AppDataTable design="new" density="dense" :columns="columns" :rows="rows" min-width="1200px" clickable @row-click="openRow">
            <template #cell-name="{ row }"><AppTableText :text="row.name" /></template>
            <template #cell-original_group_id="{ row }"><AppTableText :text="row.original_group_id" /></template>
            <template #cell-dates="{ row }"><AppTableText :text="`${dateLabel(row.tour_start)} — ${dateLabel(row.tour_end)}`" /></template>
            <template #cell-cancelled_at="{ row }"><AppTableText :text="dateLabel(row.cancelled_at)" /></template>
            <template #cell-cancelled_by_name="{ row }"><AppTableUser v-if="row.cancelled_by_name" :name="row.cancelled_by_name" /><span v-else>System</span></template>
            <template #cell-client_name="{ row }"><AppTableText :text="row.client_name" /></template>
            <template #cell-group_name="{ row }"><AppTableText :text="row.group_name" /></template>
            <template #cell-reference="{ row }"><AppTableText :text="row.reference" /></template>
            <template #cell-status="{ row }"><AppStatusBadge design="new" compact :label="refundState(row.status).name" :tone="refundState(row.status).tone" /></template>
            <template #cell-paid_amount="{ row }">{{ money(row.paid_amount, row.currency) }}</template>
            <template #cell-verified_amount="{ row }">{{ money(row.verified_amount, row.currency) }}</template>
            <template #cell-manager_name="{ row }"><AppTableUser :name="row.manager_name || ''" /></template>
          </AppDataTable>
        </AppCard>
        <app-no-results v-else design="new" :title="isRefunds ? 'No refunds found' : 'No cancelled groups found'" text="" />
        <AppPagination design="new" placement="embedded" :page="page" :page-size="25" :count="count" :visible-count="rows.length" :loading="loading" @change="changePage" />
      </template>
      <DetailModal :finance="isFinance" :selection="selection" @select="select" @close="close" @updated="load" />
    </div>
  </app-page-container>
</template>
<script>
import AppCard from '@/components/AppCard'
import AppDataTable from '@/components/AppDataTable'
import AppPagination from '@/components/AppPagination'
import AppInput from '@/components/AppInput'
import AppSelect from '@/components/AppSelect'
import AppStatusBadge from '@/components/AppStatusBadge'
import AppClearFiltersButton from '@/components/AppClearFiltersButton'
import AppContentSkeleton from '@/components/AppContentSkeleton'
import AppErrorState from '@/components/AppErrorState'
import AppTableText from '@/components/AppTableText'
import AppTableUser from '@/components/AppTableUser'
import DetailModal from './DetailModal'
import { hasSafariRole, refundOverviewRoles } from '@/utils/safariRefundAccess'
import { getCancelledSafariGroups, getSafariSnackbarRefunds, getFinanceRefunds, getUsers } from '@/http'
import { dateLabel, money, refundState, refundStates, errorMessage } from './format'
export default {
  name: 'SafariCancellationWorkspace',
  components: { AppCard, AppDataTable, AppPagination, AppInput, AppSelect, AppStatusBadge, AppClearFiltersButton, AppContentSkeleton, AppErrorState, AppTableText, AppTableUser, DetailModal },
  props: { mode: { type: String, default: 'groups' }, queue: { type: String, default: '' } },
  data() { return { rows: [], count: 0, page: 1, query: '', status: null, manager: null, managers: [], managerQuery: '', managerError: '', managerRequest: 0, managerTimer: null, searchTimer: null, loading: false, error: '', requestId: 0, states: refundStates, opener: null } },
  computed: {
    canFilterManagers() { return hasSafariRole(this.$store.state.userGroups, refundOverviewRoles) },
    isFinance() { return this.mode === 'finance-refunds' },
    isRefunds() { return ['refunds', 'finance-refunds'].includes(this.mode) },
    hasFilters() { return Boolean(this.query || this.status || this.manager) },
    managerOptions() {
      const known = this.rows.filter(row => row.manager_id).map(row => ({ id: row.manager_id, full_name: row.manager_name }))
      const options = [...this.managers, ...known, ...(this.manager ? [this.manager] : [])]
      const search = this.managerQuery.toLowerCase()
      return options.filter((item, index) => options.findIndex(option => option.id === item.id) === index && (item.full_name || '').toLowerCase().includes(search))
    },
    selection() {
      const kind = this.$route.query.refund ? 'refund' : 'group'
      const value = this.$route.query[kind]
      return /^\d+$/.test(String(value || '')) ? { kind, id: Number(value) } : null
    },
    columns() {
      return this.isRefunds ? [{ key: 'client_name', label: 'Client' }, { key: this.isFinance ? 'reference' : 'group_name', label: this.isFinance ? 'Reference' : 'Safari group' }, { key: 'paid_amount', label: 'Paid' }, { key: 'verified_amount', label: 'Refund paid' }, { key: 'manager_name', label: 'Responsible manager' }, { key: 'status', label: 'Status', width: '240px' }] : [{ key: 'name', label: 'Safari group' }, { key: 'original_group_id', label: 'Original group #' }, { key: 'dates', label: 'Tour dates' }, { key: 'participant_count', label: 'Clients', width: '80px' }, { key: 'refund_count', label: 'Refunds', width: '90px' }, { key: 'manager_name', label: 'Manager' }, { key: 'cancelled_at', label: 'Cancelled' }, { key: 'cancelled_by_name', label: 'Cancelled by' }]
    }
  },
  watch: { mode() { this.clear() }, queue() { this.clear() } },
  mounted() { this.load() },
  beforeDestroy() { this.requestId += 1; this.managerRequest += 1; clearTimeout(this.searchTimer); clearTimeout(this.managerTimer) },
  methods: {
    dateLabel,
    money,
    refundState,
    async load() {
      const current = ++this.requestId
      this.loading = true
      this.error = ''
      try {
        const fn = this.isFinance ? getFinanceRefunds : this.isRefunds ? getSafariSnackbarRefunds : getCancelledSafariGroups
        const { data } = await fn({ queue: this.isFinance ? this.queue || undefined : undefined, search: this.query || undefined, status: this.status || undefined, manager_id: this.manager ? this.manager.id : undefined, page: this.page })
        if (current === this.requestId) { this.rows = data.results; this.count = data.count }
      } catch (error) { if (current === this.requestId) this.error = errorMessage(error) } finally { if (current === this.requestId) this.loading = false }
    },
    scheduleSearch() {
      clearTimeout(this.searchTimer)
      this.requestId += 1
      this.searchTimer = setTimeout(() => this.search(), 350)
    },
    search() { clearTimeout(this.searchTimer); this.page = 1; this.load() },
    clear() { this.query = ''; this.status = null; this.manager = null; this.managerQuery = ''; this.search() },
    searchManagers(search) {
      this.managerQuery = search
      this.managerRequest += 1
      clearTimeout(this.managerTimer)
      this.managerTimer = setTimeout(() => this.loadManagers(), 250)
    },
    async loadManagers() {
      clearTimeout(this.managerTimer)
      const current = ++this.managerRequest
      this.managerError = ''
      try {
        // Default to Sales; searching also finds historical or reassigned owners.
        const { data } = await getUsers({ group: this.managerQuery ? undefined : 'sales_manager', search: this.managerQuery, page_size: 30 })
        if (current === this.managerRequest) this.managers = Array.isArray(data) ? data : data.results || []
      } catch (error) { if (current === this.managerRequest) this.managerError = errorMessage(error) }
    },
    changePage(page) { this.page = page; this.load() },
    openRow({ row, event }) { this.opener = event.currentTarget; this.select({ kind: this.isRefunds ? 'refund' : 'group', id: row.id }) },
    select({ kind, id }) {
      const query = { ...this.$route.query }
      delete query.group
      delete query.refund
      query[kind] = String(id)
      this.$router.push({ query }).catch(() => {})
    },
    close() {
      if (!this.selection) return
      const query = { ...this.$route.query }
      delete query.group
      delete query.refund
      this.$router.replace({ query }).catch(() => {})
      this.$nextTick(() => { if (this.opener && this.opener.isConnected) this.opener.focus() })
    }
  }
}
</script>
<style lang="scss">
@import './cancellations';
</style>
